import React, { useEffect, useRef } from "react";
import { MessageSquare, Plus, Trash2 } from "lucide-react";

const ChatSidebar = ({
  allConversation,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  activeSessionId,
  onLoadMore,
  hasMore,
  isLoading,
}) => {
  const sidebarRef = useRef(null);

  // Function to group chats by date
  const groupChatsByDate = (chats) => {
    const now = new Date();
    const todayDate = new Date(now);
    todayDate.setHours(0, 0, 0, 0);

    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    const sevenDaysAgoDate = new Date(todayDate);
    sevenDaysAgoDate.setDate(sevenDaysAgoDate.getDate() - 7);

    return chats?.reduce(
      (acc, chat) => {
        try {
          const chatDate = new Date(chat.last_updated);
          // Create date-only objects for comparison (ignoring time)
          const chatDateOnly = new Date(
            chatDate.getFullYear(),
            chatDate.getMonth(),
            chatDate.getDate()
          );

          if (chatDateOnly.getTime() === todayDate.getTime()) {
            acc.today.push(chat);
          } else if (chatDateOnly.getTime() === yesterdayDate.getTime()) {
            acc.yesterday.push(chat);
          } else if (chatDateOnly >= sevenDaysAgoDate) {
            acc.last7Days.push(chat);
          } else {
            acc.older.push(chat);
          }
        } catch (e) {
          console.error("Error processing chat date:", chat.last_updated, e);
          // Handle invalid dates by putting them in 'older'
          acc.older.push(chat);
        }

        return acc;
      },
      { today: [], yesterday: [], last7Days: [], older: [] }
    );
  };

  // Group chats by date
  const groupedChats = groupChatsByDate(allConversation);

  // Handle scroll events for infinite loading
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 50;

      if (isNearBottom && hasMore && !isLoading) {
        onLoadMore();
      }
    };

    sidebar.addEventListener("scroll", handleScroll);
    return () => sidebar.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoading, onLoadMore]);

  const renderChatGroup = (title, chats) => {
    if (chats.length === 0) return null;
    return (
      <div className="mb-6">
        <h3 className="text-xs font-bold text-muted-foreground mb-3 px-3 py-1 uppercase tracking-wider bg-green-300">
          {title}
        </h3>
        <div className="space-y-1">
          {chats.map((chat) => (
            <div
              key={chat.session_id}
              className={`group px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-accent text-sm relative ${
                activeSessionId === chat.session_id
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => onSelectChat(chat.session_id)}
            >
              <div className="flex items-start gap-2 pr-8">
                <MessageSquare
                  className={`h-4 w-4 mt-0.5 flex-shrink-0 opacity-60 ${
                    activeSessionId === chat.session_id
                      ? "fill-green-500 stroke-none"
                      : ""
                  }`}
                />
                <span className="truncate leading-relaxed">{chat.title}</span>
              </div>
              <button
                className="absolute right-1 top-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-opacity rounded-sm flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteChat(chat.session_id);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="w-80 bg-sidebar border-l border-sidebar-border flex flex-col h-screen">
      <div className="p-4 border-b border-sidebar-border">
        <button
          className="w-[80%] justify-start gap-3 bg-gradient-to-r from-green-400 to-blue-500 hover:from-green-500 hover:to-blue-600 text-white h-10 rounded-full px-4 flex items-center text-sm font-medium transition-all duration-200 shadow-md"
          onClick={onNewChat}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/Owl_image.jpg`}
            alt="Chat Icon"
            className="h-5 w-5 rounded-full object-cover"
          />
          New Chat
        </button>
      </div>
      <div ref={sidebarRef} className="flex-1 overflow-y-auto py-4">
        {groupedChats.today.length > 0 &&
          renderChatGroup("Today", groupedChats.today)}
        {groupedChats.yesterday.length > 0 &&
          renderChatGroup("Yesterday", groupedChats.yesterday)}
        {groupedChats.last7Days.length > 0 &&
          renderChatGroup("Last 7 Days", groupedChats.last7Days)}
        {groupedChats.older.length > 0 &&
          renderChatGroup("Older", groupedChats.older)}

        {/* Loading indicator */}
        {isLoading && (
          <div className="px-3 py-2 text-center text-sm text-muted-foreground">
            Loading more chats...
          </div>
        )}

        {/* No more chats indicator */}
        {!hasMore && allConversation.length > 0 && (
          <div className="px-3 py-2 text-center text-sm text-muted-foreground">
            No more chats to load
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;
